/**
 * Session-version interceptor — subtractive-permission close.
 *
 * Every admin response from the backend carries an
 * `X-Session-Version: <n>` header emitted by `ApiAuthService.verify`
 * in `@gozem-api/security`. The value comes from the AdminUser
 * document's `session_version` counter, which the RBAC mutation
 * controllers bump when a role edit / assignment change / role
 * delete could REMOVE tokens the admin holds.
 *
 * This interceptor tees the header into `PermissionsService.
 * syncSessionVersion()`. If a bump is detected (server value >
 * client-cached value), `syncSessionVersion` kicks a refresh of
 * the token set + returns `true`, at which point we also:
 *
 *   1. Invalidate every LookupsService cache — RBAC-scoped lookups
 *      (countries, cities, merchants) may now return fewer entries.
 *   2. Toast the admin so they know why the UI is refreshing.
 *
 * The interceptor is intentionally passive on unauthenticated
 * responses (no header emitted) and on the refresh call itself
 * (guarded by `refreshInFlight` inside PermissionsService).
 *
 * See RBAC-V2-BACKLOG.md §G for the full design + retirement plan.
 */
import {HttpInterceptorFn, HttpResponse} from "@angular/common/http";
import {inject, Injector} from "@angular/core";
import {tap} from "rxjs/operators";
import {PermissionsService} from "../services/permissions.service";
import {ToastService} from "../services/toast.service";
import {LookupsService} from "../../api/services/lookups.service";

const SESSION_VERSION_HEADER = "X-Session-Version";

export const sessionVersionInterceptor: HttpInterceptorFn = (req, next) => {
    // Resolve the Injector, NOT the services.
    //
    // `inject(PermissionsService)` here used to run on every request, including
    // the very first one the app makes. If that request was what first caused
    // `PermissionsService` to be constructed, its constructor effect calls
    // `load()`, which issues an HTTP request, which re-enters this interceptor,
    // which asks for `PermissionsService` again — while it is still hydrating.
    // Angular answers that with
    //
    //     NG0200: Circular dependency detected for `_PermissionsService`
    //
    // and the request that triggered it fails. It is order-dependent, so it
    // showed up as a page that renders with every `*canPermission` control
    // missing, or translations that silently fall back to the raw keys — both
    // observed, neither reproducible on a warm, unloaded machine.
    //
    // The header this interceptor exists for only appears on authenticated
    // admin responses, and by the time one arrives construction is long
    // finished. So take the Injector (which can never cycle) and resolve the
    // three services at response time, only when there is something to do.
    const injector = inject(Injector);

    return next(req).pipe(
        tap((event) => {
            if (!(event instanceof HttpResponse)) {
                return;
            }
            // `HttpHeaders.get` is case-insensitive per the spec —
            // works for `X-Session-Version` / `x-session-version` /
            // any casing the proxy layer happens to normalize to.
            const raw = event.headers.get(SESSION_VERSION_HEADER);
            if (!raw) {
                return;
            }

            const bumped = injector.get(PermissionsService).syncSessionVersion(raw);
            if (!bumped) {
                return;
            }

            // A subtractive bump happened: the admin's assignment /
            // role / permission set has changed on the server in a
            // way that could have REMOVED tokens. Nuke the lookups
            // cache too (RBAC-aware lookups may now return fewer
            // entries — countries, cities, merchants).
            injector.get(LookupsService).invalidate("all");
            injector
                .get(ToastService)
                .info(
                    "Vos permissions ont été mises à jour par un administrateur. " + "L'interface a été rafraîchie.",
                    "Permissions mises à jour"
                );
        })
    );
};
